import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  approveInvoice,
  formatTimestamp,
  getInvoice,
  type InvoiceWithImageUrl
} from '../lib/invoices';

export function AdminInvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<InvoiceWithImageUrl | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const refresh = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setInvoice(await getInvoice(invoiceId));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [invoiceId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (error && !invoice) return <div style={errStyle}>{error}</div>;
  if (!invoice) return <p style={{ color: 'var(--sj-text-subtle)' }}>Loading...</p>;

  const alreadyApproved = invoice.status === 'APPROVED' && invoice.isLocked;

  async function approve() {
    if (!invoiceId) return;
    if (!confirm('Approve this invoice? This locks the invoice from further edits.')) return;
    setApproving(true);
    try {
      await approveInvoice(invoiceId);
      await refresh();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setApproving(false);
    }
  }

  return (
    <div className="detail-grid">
      <div>
        <h1>Admin review</h1>
        <img
          src={invoice.imageUrl}
          alt="Invoice"
          className="invoice-image"
        />
      </div>

      <aside className="section-card">
        <h2 style={{ fontSize: '1.4rem' }}>Details</h2>
        <dl style={dl}>
          <dt style={dt}>Status</dt><dd style={dd}>{invoice.status}{invoice.isLocked ? ' · locked' : ''}</dd>
          <dt style={dt}>Submitted by</dt><dd style={dd}>{invoice.userDisplayName || invoice.userEmail}</dd>
          <dt style={dt}>User OID</dt><dd style={{ ...dd, ...mono }}>{invoice.userId}</dd>
          <dt style={dt}>Category</dt>
          <dd style={dd}>
            {invoice.category}
            {invoice.category === 'Other' && invoice.otherReason ? ` (${invoice.otherReason})` : ''}
          </dd>
          <dt style={dt}>Created</dt><dd style={dd}>{formatTimestamp(invoice.createdAt)}</dd>
          <dt style={dt}>Updated</dt><dd style={dd}>{formatTimestamp(invoice.updatedAt)}</dd>
          {invoice.approvedAt && (<><dt style={dt}>Approved</dt><dd style={dd}>{formatTimestamp(invoice.approvedAt)}</dd></>)}
          {invoice.approvedBy && (<><dt style={dt}>Approved by</dt><dd style={{ ...dd, ...mono }}>{invoice.approvedBy}</dd></>)}
        </dl>

        <div style={{ marginTop: 20 }}>
          {alreadyApproved ? (
            <p style={approvedNote}>Approved.</p>
          ) : (
            <button onClick={approve} disabled={approving} style={btnApprove}>
              {approving ? 'Approving...' : 'Approve & lock'}
            </button>
          )}
        </div>

        {error && <p style={{ ...errStyle, marginTop: 12 }}>{error}</p>}
      </aside>
    </div>
  );
}

const dl: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  rowGap: 8,
  columnGap: 14,
  fontSize: 14,
  margin: '8px 0 0'
};

const dt: React.CSSProperties = {
  color: 'var(--sj-text-muted)',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: 11,
  letterSpacing: '0.1em',
  alignSelf: 'center'
};

const dd: React.CSSProperties = {
  margin: 0,
  color: 'var(--sj-text-dark)'
};

const mono: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  wordBreak: 'break-all'
};

// Approve uses semantic success-green (not the SJ crimson) — this is a
// terminal, irreversible state change, so we want the color to read as
// "confirm safe action", not "primary brand call to action".
const btnApprove: React.CSSProperties = {
  padding: '12px 22px',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  background: 'var(--sj-success-fg)',
  color: '#fff',
  border: '1px solid var(--sj-success-fg)',
  borderRadius: 999,
  cursor: 'pointer'
};

const approvedNote: React.CSSProperties = {
  color: 'var(--sj-success-fg)',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  fontSize: 13,
  margin: 0
};

const errStyle: React.CSSProperties = {
  background: 'var(--sj-danger-bg)',
  border: '1px solid var(--sj-danger-line)',
  color: 'var(--sj-danger-fg)',
  padding: 12,
  borderRadius: 'var(--radius-xs)'
};
